import { useEffect } from 'react'
interface TransactionUpdateRule {
  id: string
  fromStatus: string
  toStatus: string
  sanctionsCheck?: string
}
const TRANSACTION_RULES: TransactionUpdateRule[] = [
  {
    id: 'TXN000001201921',
    fromStatus: 'Pending',
    toStatus: 'Completed',
    sanctionsCheck: 'Passed',
  },
  {
    id: 'TXN000002225463',
    fromStatus: 'Pending',
    toStatus: 'Rejected',
    sanctionsCheck: 'Failed',
  },
  {
    id: 'TXN000004478520',
    fromStatus: 'In-Process',
    toStatus: 'Completed',
  },
]
const TWO_MINUTES_MS = 2 * 60 * 1000 // 2 minutes in milliseconds
const FORTY_FIVE_SECONDS_MS = 45 * 1000 // 45 seconds in milliseconds
export const useTransactionAutoUpdate = () => {
  useEffect(() => {
    const checkAndUpdateTransactions = () => {
      try {
        const stored = localStorage.getItem('initiatedTransactions')
        if (!stored) return
        const transactions = JSON.parse(stored)
        let hasUpdates = false
        const updatedTransactions = transactions.map((transaction: any) => {
          // Handle checker-approved transactions (45 second delay)
          if (
            transaction.status === 'Pending' &&
            transaction.checkerTimestamp
          ) {
            const checkerTime = new Date(transaction.checkerTimestamp).getTime()
            const currentTime = new Date().getTime()
            const elapsedTime = currentTime - checkerTime
            if (elapsedTime >= FORTY_FIVE_SECONDS_MS) {
              hasUpdates = true
              const finalStatus =
                transaction.checkerDecision === 'completed'
                  ? 'Completed'
                  : 'Rejected'
              return {
                ...transaction,
                status: finalStatus,
                overview: {
                  ...transaction.overview,
                  status: finalStatus,
                },
                timeline: updateTimelineForFinalStatus(
                  transaction.timeline,
                  finalStatus,
                ),
              }
            }
          }
          // Find matching rule for this transaction
          const rule = TRANSACTION_RULES.find((r) => r.id === transaction.id)
          if (!rule) return transaction
          // Check if transaction matches the rule's criteria
          if (transaction.status !== rule.fromStatus) return transaction
          // Check sanctions check if specified in rule
          if (
            rule.sanctionsCheck &&
            transaction.sanctionsCheck !== rule.sanctionsCheck
          ) {
            return transaction
          }
          // Calculate time elapsed since initiation
          const initiatedTime = new Date(transaction.timestamp).getTime()
          const currentTime = new Date().getTime()
          const elapsedTime = currentTime - initiatedTime
          // If 2 minutes have passed, update the status
          if (elapsedTime >= TWO_MINUTES_MS) {
            hasUpdates = true
            return {
              ...transaction,
              status: rule.toStatus,
              overview: {
                ...transaction.overview,
                status: rule.toStatus,
              },
              // Update compliance status based on new status
              compliance: {
                ...transaction.compliance,
                sanctionsCheck:
                  rule.toStatus === 'Rejected'
                    ? 'Failed'
                    : transaction.compliance?.sanctionsCheck || 'Passed',
                amlStatus:
                  rule.toStatus === 'Rejected'
                    ? 'Failed'
                    : transaction.compliance?.amlStatus || 'Clear',
              },
              // Update timeline
              timeline: updateTimeline(transaction.timeline, rule.toStatus),
              // Update actions
              actions: updateActions(transaction.actions, rule.toStatus),
            }
          }
          return transaction
        })
        // Only update localStorage and dispatch event if there were changes
        if (hasUpdates) {
          localStorage.setItem(
            'initiatedTransactions',
            JSON.stringify(updatedTransactions),
          )
          // Dispatch custom event to notify components
          window.dispatchEvent(new Event('transactionUpdated'))
        }
      } catch (error) {
        console.error('Error updating transactions:', error)
      }
    }
    // Helper function to update timeline for final status
    const updateTimelineForFinalStatus = (
      timeline: any[],
      finalStatus: string,
    ) => {
      if (!timeline) return []
      const currentDate = new Date().toLocaleString()
      if (finalStatus === 'Completed') {
        return timeline.map((item) => {
          if (item.status === 'pending' || item.status === 'current') {
            return {
              ...item,
              status: 'completed',
              timestamp: currentDate,
            }
          }
          return item
        })
      } else if (finalStatus === 'Rejected') {
        return timeline.map((item, index) => {
          if (index === 0) return item // Keep first item as is
          if (index === 1) {
            return {
              ...item,
              status: 'error',
              description: 'Transaction rejected by checker',
              timestamp: currentDate,
            }
          }
          return {
            ...item,
            status: 'cancelled',
          }
        })
      }
      return timeline
    }
    // Helper function to update timeline
    const updateTimeline = (timeline: any[], newStatus: string) => {
      if (!timeline) return []
      const currentDate = new Date().toLocaleString()
      if (newStatus === 'Completed') {
        return timeline.map((item) => {
          if (item.status === 'pending' || item.status === 'current') {
            return {
              ...item,
              status: 'completed',
              timestamp: currentDate,
            }
          }
          return item
        })
      } else if (newStatus === 'Rejected') {
        return [
          ...timeline.slice(0, 2),
          {
            title: 'Compliance Check',
            description: 'Transaction flagged',
            timestamp: currentDate,
            status: 'error',
          },
          {
            title: 'Transaction Rejected',
            description: 'Transaction rejected after review',
            timestamp: currentDate,
            status: 'error',
          },
        ]
      }
      return timeline
    }
    // Helper function to update actions
    const updateActions = (actions: any[], newStatus: string) => {
      if (!actions) return []
      const currentDate = new Date().toLocaleString()
      if (newStatus === 'Completed') {
        const completedActions = actions.map((action) => {
          if (action.status === 'pending') {
            return {
              ...action,
              status: 'completed',
              timestamp: currentDate,
            }
          }
          return action
        })
        // Add final completion actions if not already present
        const hasApproval = completedActions.some(
          (a) => a.action === 'Approved by Checker',
        )
        const hasPayment = completedActions.some(
          (a) => a.action === 'Payment Processed',
        )
        const hasCompletion = completedActions.some(
          (a) => a.action === 'Transaction Completed',
        )
        if (!hasApproval) {
          completedActions.push({
            id: completedActions.length + 1,
            action: 'Approved by Checker',
            performedBy: 'Checker',
            timestamp: currentDate,
            details: 'Transaction verified and approved',
            status: 'completed',
          })
        }
        if (!hasPayment) {
          completedActions.push({
            id: completedActions.length + 1,
            action: 'Payment Processed',
            performedBy: 'System',
            timestamp: currentDate,
            details: 'Funds transferred successfully',
            status: 'completed',
          })
        }
        if (!hasCompletion) {
          completedActions.push({
            id: completedActions.length + 1,
            action: 'Transaction Completed',
            performedBy: 'System',
            timestamp: currentDate,
            details: 'Receiver notified of successful payment',
            status: 'completed',
          })
        }
        return completedActions
      } else if (newStatus === 'Rejected') {
        return [
          ...actions.slice(0, 2),
          {
            id: 3,
            action: 'Sanctions Check Failed',
            performedBy: 'System',
            timestamp: currentDate,
            details: 'Transaction flagged for review',
            status: 'error',
          },
          {
            id: 4,
            action: 'Transaction Rejected',
            performedBy: 'Compliance Team',
            timestamp: currentDate,
            details: 'Transaction rejected after review',
            status: 'error',
          },
        ]
      }
      return actions
    }
    // Run check immediately on mount
    checkAndUpdateTransactions()
    // Set up interval to check every 10 seconds
    const interval = setInterval(checkAndUpdateTransactions, 10000)
    return () => clearInterval(interval)
  }, [])
}
